import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { observer } from 'mobx-react-lite';

import {
  CircularProgressBar,
  GoalReached,
  IHeadCell,
  RemainingTime,
} from '@trader/components';
import { usePortfolioLocal } from '@trader/hooks';
import { getGoalReached, getOverAllPl } from '@trader/utils';
import { EChallenge, EChallengeResult, EChallengeStatus } from '@trader/types';
import { TChallengeEntity, TTradingAccountEntity, useMst } from '@trader/store';

import { useRenderCellActions } from '../../hooks';

import * as Styled from '../../styled';

interface IChallenge {
  challenge: TChallengeEntity;
  cells: Array<IHeadCell>;
  leftAction?: (challengeId: number) => JSX.Element;
  rightAction?: (challengeId: number) => JSX.Element;
  shouldDisplayAccTypeIcon?: boolean;
}

const initState: TChallengeEntity = {
  id: 0,
  status: EChallengeStatus.Disable,
  hierarchyType: EChallenge.NonAssigned,
  masterId: null,
  accountId: 0,
  challengeAmount: 0,
  purchaseAmount: 0,
  payout: 0,
  balance: 0,
  profitLoss24: 0,
  profitTarget: 0,
  currencySymbol: '',
  name: '',
  profitLossOverall: 0,
  tradingDays: 0,
  minTradingDays: 0,
  remainingTime: 0,
  goalReached: 0,
  result: EChallengeResult.Cancelled,
  blockedDueToDailyTarget: false,
};

export const Challenge: React.FC<IChallenge> = observer(
  ({ challenge, cells, leftAction, rightAction, shouldDisplayAccTypeIcon }) => {
    const store = useMst();
    const theme = useTheme();

    const [state, setState] = useState(challenge);

    const account = store.entities.tradingAccounts.get<TTradingAccountEntity>(
      challenge.accountId
    );

    const {
      renderAccountIcon,
      renderProfitLoss,
      renderStatus,
      renderChangeAcc,
      renderResult,
    } = useRenderCellActions(state, account, shouldDisplayAccTypeIcon);

    usePortfolioLocal(account?.platformLogin, account?.accountType, message => {
      if (Number(account?.platformLogin) === Number(message.login)) {
        setState(prev => ({
          ...prev,
          balance: message.balance,
          profitLossOverall: getOverAllPl(
            prev.balance,
            prev.challengeAmount,
            message.pl
          ),
          goalReached: getGoalReached(
            prev.profitTarget,
            getOverAllPl(prev.balance, prev.challengeAmount, message.pl),
            prev.challengeAmount
          ),
        }));
      }
    });

    useEffect(() => {
      return () => {
        setState(initState);
      };
    }, []);

    const renderRow = {
      trade: state.hierarchyType !== EChallenge.Slave && renderChangeAcc(),
      leftAction: leftAction && leftAction(state.id),
      accountIcon: renderAccountIcon(),
      id: state.hierarchyType !== EChallenge.Slave && (
        <Styled.Value>{state.id}</Styled.Value>
      ),
      status: renderStatus(),
      dailyTargetProfit: (
        <Styled.DailyTargetProfitIcon
          iconType='checkmark'
          $achieved={state.blockedDueToDailyTarget}
        />
      ),
      accountId: (
        <Styled.Value>
          {account.isInUse && <Styled.ActiveAccIcon iconType='activeAcc' />}
          {state.accountId}
        </Styled.Value>
      ),
      challengeAmount: (
        <Styled.Value>
          {state.currencySymbol}
          {state.challengeAmount}
        </Styled.Value>
      ),
      payout: <Styled.Value>{state.payout}%</Styled.Value>,
      balance: (
        <Styled.Value>
          {state.currencySymbol}
          {state.balance}
        </Styled.Value>
      ),
      profitLoss24: renderProfitLoss(state.profitLoss24),
      profitLossOverall: renderProfitLoss(state.profitLossOverall),
      tradingDays: (
        <Styled.Value>
          <CircularProgressBar
            value={state.tradingDays}
            maxValue={7}
            text={`${state.tradingDays}/${state.minTradingDays}`}
            pathColor={theme.palette.purchaseButtons.green}
          />
        </Styled.Value>
      ),
      remainingTime: <RemainingTime value={state.remainingTime} />,
      goalReached: <GoalReached value={state.goalReached} hasProgressBar />,
      result: renderResult(),
      rightAction: rightAction && rightAction(state.id),
    };

    return (
      <Styled.Row
        $isMaster={state.hierarchyType === EChallenge.Master}
        className='challenge'
      >
        {cells.map(cell => (
          <span
            style={{ justifyContent: cell.align, minWidth: cell.minWidth }}
            key={`${cell.label}-${cell.id}`}
          >
            {renderRow[cell.id]}
          </span>
        ))}
      </Styled.Row>
    );
  }
);
